import { ExtraScopeEnum } from '@koob/enums';

/**
 * A place, as the API carries it. A transfer arrives at a point of interest as
 * often as at a city — an airport has no city relation at all — so `cityId` and
 * `cityLocation` are both optional, and a caller that needs a city must treat
 * their absence as an answer rather than as a bug.
 */
export interface Position {
  id?: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  lat?: number;
  lon?: number;
  isCity?: boolean;
  cityId?: string;
  cityLocation?: { id?: string };
}

/**
 * The experience behind a retained option. This is the only place the transfer
 * typology and the itinerary positions live: the saved trip carries neither.
 * Spread wholesale into the hydrated extra, hence the index signature.
 */
export interface ExtraExperience {
  id?: string;
  experienceType?: string;
  transfer?: unknown;
  startPosition?: Position;
  endPosition?: Position;
  [field: string]: unknown;
}

/**
 * A retained option as a saved trip returns it: ids, a day, a parent — and
 * nothing else. `optionId` names the experience; `id` is the record's own.
 */
export interface TripOption {
  id?: string;
  optionId?: string;
  /** 1-based, as saved. */
  dayIndex?: number;
  parent?: { option?: { id?: string } };
  /**
   * KD-3650 — which retention of the service this line is, the same service
   * being retainable several times on a day. Absent on every line saved before
   * that ticket: the column is nullable and never backfilled.
   */
  occurrenceKey?: string;
}

/** A retained option once it knows which experience retained it, and how. */
interface ScopedTripOption extends TripOption {
  experienceId: string | null;
  scope: string;
}

export interface TripDay {
  /** 1-based, as saved. */
  dayIndex?: number;
  experienceId?: string | null;
  options?: TripOption[];
  [field: string]: unknown;
}

export interface Trip {
  tripDays?: TripDay[];
  globalOptions?: TripOption[];
}

/** The shape `getDayEffectiveEndPosition` reads, same as the composer store's. */
export interface SelectedExtra extends ExtraExperience {
  experienceId: string | null;
  /** 0-based, as read everywhere downstream. */
  dayIndex: number;
  scope: string;
  /** KD-3650 — carried from the saved line, see `TripOption.occurrenceKey`. */
  occurrenceKey?: string;
}

export type TripDayWithSelectedExtras = TripDay & {
  selectedExtras: SelectedExtra[];
};

/** The experience details of a trip's options, keyed by experience id. */
export type ExtraExperiences = Record<string, ExtraExperience | undefined>;

/**
 * KD-3548 — the extras retained on a trip, read back from a saved itinerary.
 *
 * The export renders the trip exactly as the API returns it, where the retained
 * options are split across two places: `trip.globalOptions` for the ones scoped
 * to a whole experience, `tripDays[].options` for the ones scoped to a single
 * day. Both carry a 1-based `dayIndex` and little else — the saved record keeps
 * only the ids, the pax counts and the parent.
 *
 * Same reading as `formatTripFromApi` in the Trip Designer, deliberately: the
 * composer and the PDF must not disagree on which extras a day holds.
 */
function getTripExtras(trip?: Trip): ScopedTripOption[] {
  const dayToExperienceId: Record<number, string> = {};
  (trip?.tripDays ?? []).forEach((day) => {
    if (day?.experienceId && day?.dayIndex != null) {
      dayToExperienceId[day.dayIndex] = day.experienceId;
    }
  });

  return [
    ...(trip?.globalOptions ?? []).map((option) => ({
      ...option,
      // A global option names the day it belongs to, not the experience.
      experienceId:
        (option?.dayIndex != null
          ? dayToExperienceId[option.dayIndex]
          : undefined) ?? null,
      scope: ExtraScopeEnum.GLOBAL,
    })),
    ...(trip?.tripDays ?? []).flatMap((day) =>
      (day?.options ?? []).map((option) => ({
        ...option,
        experienceId: day?.experienceId ?? null,
        scope: ExtraScopeEnum.DAY,
      })),
    ),
  ];
}

/**
 * The experiences to fetch to know what those extras actually are.
 *
 * The saved record carries no typology and no itinerary positions, so a retained
 * transfer cannot be recognised — let alone chained — until its experience
 * detail is loaded. Deduplicated: the same transfer is routinely retained on
 * several days of the same trip.
 */
export function getExtraExperienceIds(trip?: Trip): string[] {
  return [
    ...new Set(
      getTripExtras(trip)
        .map((option) => option?.optionId ?? option?.id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
}

/**
 * The trip days, each carrying its retained extras in the shape the shared
 * `getDayEffectiveEndPosition` reads — the same `selectedExtras` the composer
 * store holds.
 *
 * An extra whose experience could not be fetched is dropped rather than kept
 * empty: with no typology and no positions it can only be mistaken for
 * something it is not.
 */
export function getDaysWithSelectedExtras(
  trip?: Trip,
  extraExperiences?: ExtraExperiences,
): TripDayWithSelectedExtras[] {
  const extrasByDayIndex: Record<number, SelectedExtra[]> = {};

  getTripExtras(trip).forEach((option) => {
    const detail = extraExperiences?.[option?.optionId ?? option?.id ?? ''];
    if (!detail) {
      return;
    }

    // Saved 1-based, read 0-based everywhere downstream.
    const dayIndex = (option?.dayIndex ?? 0) - 1;
    if (!extrasByDayIndex[dayIndex]) {
      extrasByDayIndex[dayIndex] = [];
    }

    // `experienceId` and `scope` are what place the extra in the itinerary: the
    // first says which experience retained it, the second whether it belongs to
    // one day or to the whole experience. Both decide which day it moves.
    //
    // KD-3650 — `detail` is the experience, shared by every retention of the
    // same service: two occurrences hydrate into two entries that are equal
    // field for field. `occurrenceKey` is the only thing that still tells them
    // apart downstream, so it is carried over rather than left on the saved
    // line, which the export never sees again.
    extrasByDayIndex[dayIndex].push({
      ...detail,
      experienceId: option.experienceId,
      dayIndex,
      scope: option.scope,
      occurrenceKey: option.occurrenceKey,
    });
  });

  return (trip?.tripDays ?? []).map((day, index) => ({
    ...day,
    selectedExtras: extrasByDayIndex[index] ?? [],
  }));
}

/**
 * The experiences of the trip, keyed by id.
 *
 * `getDayEffectiveEndPosition` looks the next day's experience up by id — it is
 * that one which tells whether the day being rendered is the last of its
 * experience, and it can declare the transfer itself.
 */
export function getExperiencesMap<T extends { id?: string }>(
  experiences?: (T | null | undefined)[],
): Record<string, T> {
  const map: Record<string, T> = {};

  (experiences ?? []).forEach((experience) => {
    if (experience?.id) {
      map[experience.id] = experience;
    }
  });

  return map;
}
